// src/utils/markdownLoader.js
import { useState, useEffect } from 'react';

// Your existing parseMarkdown function (keep as is)
const parseMarkdown = (markdown) => {
  if (!markdown) return '';
  
  try {
    // Replace headings with IDs for linking
    let html = markdown
      .replace(/^### (.*$)/gim, function(match, title) {
        try {
          const safeTitle = title || '';
          const id = safeTitle.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
          return `<h3 id="${id}">${title}</h3>`;
        } catch (e) {
          console.error('Error parsing h3:', e);
          return `<h3>${title}</h3>`;
        }
      })
      .replace(/^## (.*$)/gim, function(match, title) {
        try {
          const safeTitle = title || '';
          const id = safeTitle.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
          return `<h2 id="${id}">${title}</h2>`;
        } catch (e) {
          console.error('Error parsing h2:', e);
          return `<h2>${title}</h2>`;
        }
      })
      .replace(/^# (.*$)/gim, function(match, title) {
        try {
          const safeTitle = title || '';
          const id = safeTitle.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
          return `<h1 id="${id}">${title}</h1>`;
        } catch (e) {
          console.error('Error parsing h1:', e);
          return `<h1>${title}</h1>`;
        }
      });
    
    // Process the rest of markdown with error handling for each step
    try {
      html = html.replace(/^\> (.*)$/gim, '<blockquote>$1</blockquote>');
    } catch (e) {
      console.error('Error parsing blockquotes:', e);
    }
    
    try {
      html = html.replace(/^\- (.*)$/gim, '<ul><li>$1</li></ul>');
    } catch (e) {
      console.error('Error parsing lists:', e);
    }
    
    try {
      html = html.replace(/^(?!<h|<ul|<li|<blockquote)(.+)$/gim, '<p>$1</p>');
    } catch (e) {
      console.error('Error parsing paragraphs:', e);
    }
    
    try {
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    } catch (e) {
      console.error('Error parsing emphasis:', e);
    }
    
    try {
      html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
    } catch (e) {
      console.error('Error parsing links:', e);
    }
    
    try {
      html = html.replace(/\n\n/gim, '<br/>');
    } catch (e) {
      console.error('Error parsing line breaks:', e);
    }
    
    try {
      html = html.replace(/<\/ul>\s*<ul>/gim, '');
    } catch (e) {
      console.error('Error combining lists:', e);
    }
    
    // Basic HTML sanitization
    html = html
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/&lt;(\/?)h([1-6])&gt;/g, '<$1h$2>')
      .replace(/&lt;(\/?)p&gt;/g, '<$1p>')
      .replace(/&lt;(\/?)strong&gt;/g, '<$1strong>')
      .replace(/&lt;(\/?)em&gt;/g, '<$1em>')
      .replace(/&lt;(\/?)blockquote&gt;/g, '<$1blockquote>')
      .replace(/&lt;(\/?)ul&gt;/g, '<$1ul>')
      .replace(/&lt;(\/?)li&gt;/g, '<$1li>')
      .replace(/&lt;(\/?)br\/&gt;/g, '<$1br/>')
      .replace(/&lt;a href="([^"]+)"&gt;/g, '<a href="$1">')
      .replace(/&lt;\/a&gt;/g, '</a>')
      .replace(/&lt;h([1-6]) id="([^"]+)"&gt;/g, '<h$1 id="$2">');
    
    return html;
  } catch (error) {
    console.error('Error in markdown parsing:', error);
    return `<p>Error rendering content. Please check the markdown format.</p>
            <pre>${markdown ? markdown.substring(0, 500).replace(/</g, '&lt;').replace(/>/g, '&gt;') : ''}</pre>`;
  }
};

// Your existing extractTableOfContents function (keep as is)
export const extractTableOfContents = (markdown) => {
  if (!markdown || typeof markdown !== 'string') {
    return [];
  }
  
  try {
    const headings = [];
    const headingRegex = /^(#{2,3})\s+(.+)$/gm;
    let match;
    
    while ((match = headingRegex.exec(markdown)) !== null) {
      const level = match[1].length;
      const title = match[2].trim();
      const id = title.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
      
      headings.push({
        id,
        title,
        level
      });
    }
    
    return headings;
  } catch (error) {
    console.error('Error extracting table of contents:', error);
    return [];
  }
};

// Updated hook with better error handling and fallback
export const useMarkdownContent = (contentFile) => {
  const [state, setState] = useState({
    content: '',
    toc: [],
    loading: true,
    error: null,
    rawMarkdown: ''
  });
  
  useEffect(() => {
    if (!contentFile) {
      setState({
        content: '',
        toc: [],
        loading: false,
        error: 'No content file specified',
        rawMarkdown: ''
      });
      return;
    }
    
    const fetchContent = async () => {
      try {
        // Try multiple paths in case of different hosting setups
        const possiblePaths = [
          `/content/blog/${contentFile}`,
          `/public/content/blog/${contentFile}`,
          `/assets/content/blog/${contentFile}`,
          `./content/blog/${contentFile}`
        ];
        
        let response = null;
        let text = null;
        
        // Try each path until one works
        for (const path of possiblePaths) {
          try {
            response = await fetch(path);
            if (response.ok) {
              text = await response.text();
              break;
            }
          } catch (err) {
            console.warn(`Failed to fetch from ${path}:`, err.message);
            continue;
          }
        }
        
        // If no path worked, throw an error
        if (!response || !response.ok || !text) {
          throw new Error(`Failed to fetch markdown file: ${contentFile}. Tried multiple paths but none worked.`);
        }
        
        // Validate content
        if (!text || typeof text !== 'string' || text.trim().length === 0) {
          throw new Error('Invalid or empty markdown content received');
        }
        
        const toc = extractTableOfContents(text);
        const parsedContent = parseMarkdown(text);
        
        setState({
          content: parsedContent,
          toc,
          loading: false,
          error: null,
          rawMarkdown: text
        });
        
      } catch (err) {
        console.error('Error loading markdown:', err);
        
        // Provide a more user-friendly error message
        const errorMessage = err.message.includes('403') 
          ? `Content temporarily unavailable. Please try again later.`
          : `Failed to load content: ${contentFile}`;
        
        setState({
          content: `<div class="error-message">
            <h2>Content Loading Error</h2>
            <p>${errorMessage}</p>
            <p>This might be a temporary issue. Please refresh the page or try again later.</p>
          </div>`,
          toc: [],
          loading: false,
          error: err.message,
          rawMarkdown: ''
        });
      }
    };
    
    fetchContent();
  }, [contentFile]);
  
  return state;
};

// Updated component with better error display
export const MarkdownContent = ({ contentFile }) => {
  const { content, loading, error } = useMarkdownContent(contentFile);
  
  if (loading) {
    return (
      <div className="py-8 px-6 bg-slate-50 rounded-lg animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-3/4 mb-4"></div>
        <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
        <div className="h-4 bg-slate-200 rounded w-5/6"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="py-6 px-6 bg-red-50 text-red-800 rounded-lg border border-red-200">
        <h3 className="font-semibold mb-2">Content Unavailable</h3>
        <p className="text-sm">We're having trouble loading this content. Please try refreshing the page.</p>
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-4">
            <summary className="cursor-pointer text-xs">Technical Details (Dev Only)</summary>
            <pre className="text-xs mt-2 p-2 bg-red-100 rounded">{error}</pre>
          </details>
        )}
      </div>
    );
  }
  
  return (
    <div 
      className="prose prose-lg max-w-none"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};